import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui';
import { useSettingsHref } from '../admin/DealerWorkspaceContext';

/**
 * SettingsNotFound — the settings-nested 404 (UX-12).
 *
 * Rendered by `<Route path="*" />` inside `<Route path="settings" element={<SettingsLayout />}>`,
 * so `SettingsLayout`'s outlet places this in the settings content column and
 * the sub-nav rail stays visible. The pattern the operator asked for:
 *
 *   "no way back in but clicking Settings again"  ← the today defect
 *   "same sub-nav, familiar shell, one-click to somewhere real"  ← what this fixes
 *
 * Visual language matches `common/ErrorPage`'s 404 variant. The affordance
 * differs — takes the user to the settings landing rather than the app
 * dashboard, because they were clearly trying to be in settings.
 */
export default function SettingsNotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const href = useSettingsHref(); // IDI-63
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md px-6">
        <MagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-6xl font-bold text-primary-600 mb-2">404</p>
        <h1 style={{ font: 'var(--text-h1)', color: 'var(--color-text-primary)', marginBottom: '0.5rem' }}>
          Settings page not found
        </h1>
        <p className="text-gray-500 mb-2">
          We couldn&apos;t find a settings page at{' '}
          <code style={{ color: 'var(--color-text-primary)' }}>{location.pathname}</code>.
        </p>
        <p className="text-gray-500 mb-6">
          Pick something from the list on the left, or head back to the main settings page.
        </p>
        <Button onClick={() => navigate(href('/settings/company'))}>
          Go to settings
        </Button>
      </div>
    </div>
  );
}
